import { useEffect } from 'react';

import { getAccountBannerContent, getPendingCtaLabel } from '../../utils/renewal';
import { RENEWAL_STEPS, RENEWAL_SURFACES } from '../../utils/analytics/renewalStep';
import { useRenewalStatus } from './RenewalStatusProvider';
import useTrackRenewalStep from './useTrackRenewalStep';
import RenewalIllustration, { RenewalWordmark } from './RenewalIllustration';

import './RenewalAccountBanner.scss';

/**
 * The pink renewal card on the Account page, above Refer a Friend (Figma
 * "Banner – …", 11828:17251 / 11817:13141 / 11823:9109). Persistent: shown on
 * every visit while the state lasts (D6), unlike the once-per-session entry sheet.
 *
 * @return {JSX.Element|null}
 */
const RenewalAccountBanner = () => {
  const {
    status, runAction, isActionPending, pendingAction,
  } = useRenewalStatus();
  const content = getAccountBannerContent(status);
  const trackStep = useTrackRenewalStep();

  // Once per message (a new state is a new banner), not on every re-render.
  const bannerTitle = content?.title;
  useEffect(() => {
    if (bannerTitle) {
      trackStep(RENEWAL_STEPS.BANNER_SHOWN, RENEWAL_SURFACES.ACCOUNT_BANNER);
    }
  }, [bannerTitle, trackStep]);

  if (!content) return null;

  return (
    <section className="RenewalAccountBanner" aria-label="Membership status">
      <RenewalWordmark />
      <div className="RenewalAccountBanner__text">
        <h3 className="RenewalAccountBanner__title">{content.title}</h3>
        <RenewalIllustration illustration={content.illustration} slot="banner" />
      </div>
      <p className="RenewalAccountBanner__body">{content.body}</p>
      {content.ctaLabel && (
        <button
          type="button"
          className="RenewalAccountBanner__cta"
          onClick={() => {
            trackStep(RENEWAL_STEPS.CTA_CLICKED, content.action);
            runAction(content.action);
          }}
          disabled={isActionPending}
          aria-busy={pendingAction === content.action}
        >
          {(pendingAction === content.action && getPendingCtaLabel(content.action, status))
            || content.ctaLabel}
        </button>
      )}
      {content.footnote && <p className="RenewalAccountBanner__footnote">{content.footnote}</p>}
    </section>
  );
};

export default RenewalAccountBanner;
